import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { useReviewBootstrap } from "@frontend/pages/case-workspace/hooks/use-review-bootstrap"
import { useSubmissionHistorySync } from "@frontend/pages/case-workspace/hooks/use-submission-history-sync"
import { sortCandidateSubmissions } from "@frontend/pages/case-workspace/pathology/components/module-schema"
import { buildLabelRasterManifestEntries } from "@frontend/pages/case-workspace/pathology/lib/label-raster-manifests"
import type {
  BootstrapSessionResult,
  Submission,
  SubmissionListResponse
} from "@frontend/pages/case-workspace/pathology/types"
import { useAnnotationStore } from "@frontend/stores/annotation-store"
import { useHistoryStore } from "@frontend/stores/history-store"
import type { AnnotationSnapshotV1 } from "@shared/lib/history-types"
import { decodeSessionCbor } from "@shared/lib/session-cbor"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"

interface UsePathologySessionParams {
  api: (input: string, init?: RequestInit) => Promise<unknown>
  caseId?: string
  projectId?: string
  activeModuleId: string | null
  isActiveModuleComplete: boolean
  setModuleCompletion: (params: { projectModuleId: string; completed: boolean; completedAt: string | null }) => void
  reviewSubmissionId?: string | null
  currentUserRole?: "instance_admin" | "project_admin" | "annotator" | "reviewer" | "viewer" | null
}

export function usePathologySession({
  api,
  caseId,
  projectId,
  activeModuleId,
  isActiveModuleComplete,
  setModuleCompletion,
  reviewSubmissionId = null,
  currentUserRole = null
}: UsePathologySessionParams) {
  const queryClient = useQueryClient()
  const { getAccessToken } = useAuthRuntime()
  const isReviewMode = Boolean(reviewSubmissionId)
  const isNonAnnotator = Boolean(currentUserRole) && currentUserRole !== "annotator"
  const [viewerSessionState, setViewerSessionState] = useState<{
    submissionId: string | null
    pendingRestoreSnapshot: AnnotationSnapshotV1 | null
  }>({
    submissionId: null,
    pendingRestoreSnapshot: null
  })

  const submissionId = viewerSessionState.submissionId
  const _pendingRestoreSnapshot = viewerSessionState.pendingRestoreSnapshot

  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessToken()
    return token ? { Authorization: `Bearer ${token}` } : undefined
  }, [getAccessToken])

  // ── Review mode bootstrap ──
  const reviewBootstrap = useReviewBootstrap({
    api,
    reviewSubmissionId: reviewSubmissionId ?? "",
    caseId
  })
  const [reviewStatus, setReviewStatus] = useState<string | null>(null)

  useEffect(() => {
    if (isReviewMode && reviewBootstrap.submission) {
      setReviewStatus(reviewBootstrap.submission.status)
    }
  }, [isReviewMode, reviewBootstrap.submission])

  const reviewEditEnabled = isReviewMode ? reviewBootstrap.editEnabled : false

  const bootstrapQuery = useQuery<BootstrapSessionResult>({
    queryKey: ["pathology-annotation-bootstrap", caseId, projectId, currentUserRole],
    enabled: Boolean(caseId) && Boolean(projectId) && !isReviewMode,
    staleTime: Infinity,
    refetchOnMount: "always",
    queryFn: async ({ signal }) => {
      if (!caseId) {
        throw new Error("Missing case id")
      }

      const list = (await api(`/cases/${caseId}/submissions?status=in_progress&mine=true&page=1&limit=100`, {
        signal
      })) as SubmissionListResponse
      const candidates = sortCandidateSubmissions(list.data ?? [])

      let selected = candidates[0] ?? null
      if (!selected) {
        const all = (await api(`/cases/${caseId}/submissions?mine=true&page=1&limit=100`, {
          signal
        })) as SubmissionListResponse
        selected = sortCandidateSubmissions(all.data ?? [])[0] ?? null
      }

      if (!selected && isNonAnnotator) {
        return { submission: null, historyPayload: null }
      }

      if (!selected) {
        selected = (await api(`/cases/${caseId}/submissions`, {
          method: "POST",
          signal
        })) as Submission
      }

      const headers = await getAuthHeaders()
      const historyRes = await fetch(`/api/submissions/${selected.id}/history/file`, {
        headers,
        signal
      })

      return {
        submission: selected,
        historyPayload: historyRes.ok ? await historyRes.arrayBuffer() : null
      }
    }
  })

  const isBootstrappingSession = isReviewMode
    ? reviewBootstrap.isBootstrapping
    : bootstrapQuery.isPending || bootstrapQuery.isFetching
  const effectiveSubmissionId = isReviewMode ? reviewSubmissionId : submissionId
  const effectiveGetAuthHeaders = isReviewMode ? reviewBootstrap.getAuthHeaders : getAuthHeaders

  const { uploadHistoryNow, isHistoryDirty, isUploadingHistory, checkpointCount } = useSubmissionHistorySync({
    submissionId: effectiveSubmissionId,
    caseId,
    isBootstrappingSession,
    getAuthHeaders: effectiveGetAuthHeaders,
    isReadOnly: isReviewMode && !reviewEditEnabled,
    generateLabelRasterManifests: buildLabelRasterManifestEntries
  })

  const applySnapshot = useCallback((snapshot: AnnotationSnapshotV1 | null) => {
    if (!snapshot) return
    useAnnotationStore.getState().replaceFromSnapshot(snapshot)
    setViewerSessionState((prev) => ({
      ...prev,
      pendingRestoreSnapshot: snapshot
    }))
  }, [])

  const handleTimelineScrub = useCallback(
    (index: number) => {
      applySnapshot(useHistoryStore.getState().seekToCursor(index))
    },
    [applySnapshot]
  )

  const setModuleCompletionRemote = useCallback(
    async (moduleId: string, completed: boolean) => {
      if (!effectiveSubmissionId) return

      setModuleCompletion({
        projectModuleId: moduleId,
        completed,
        completedAt: completed ? new Date().toISOString() : null
      })

      try {
        await api(`/submissions/${effectiveSubmissionId}/module-completions/${moduleId}`, {
          method: "PUT",
          body: JSON.stringify({ completed })
        })
      } catch (err) {
        setModuleCompletion({
          projectModuleId: moduleId,
          completed: !completed,
          completedAt: null
        })
        throw err
      }
    },
    [api, effectiveSubmissionId, setModuleCompletion]
  )

  const markActiveModuleComplete = useCallback(async () => {
    if (!(effectiveSubmissionId && activeModuleId)) return
    await setModuleCompletionRemote(activeModuleId, !isActiveModuleComplete)
  }, [activeModuleId, effectiveSubmissionId, isActiveModuleComplete, setModuleCompletionRemote])

  useEffect(() => {
    if (!(caseId && bootstrapQuery.data)) return

    const { submission, historyPayload } = bootstrapQuery.data

    // View-only mode: no submission, just reset stores
    if (!submission) {
      useAnnotationStore.getState().reset()
      useHistoryStore.getState().reset()
      setViewerSessionState({ submissionId: null, pendingRestoreSnapshot: null })
      return
    }

    const session = historyPayload ? decodeSessionCbor(historyPayload) : null

    if (session) {
      useHistoryStore.getState().loadSession(session)
      const snapshot = session.checkpoints[session.cursor]?.snapshot
      if (snapshot) {
        useAnnotationStore.getState().replaceFromSnapshot(snapshot)
      }
      setViewerSessionState({
        submissionId: submission.id,
        pendingRestoreSnapshot: snapshot ?? null
      })
      return
    }

    useAnnotationStore.getState().reset()
    useHistoryStore.getState().initSession({
      submissionId: submission.id,
      caseId,
      initialSnapshot: useAnnotationStore.getState().getSnapshot()
    })
    setViewerSessionState({
      submissionId: submission.id,
      pendingRestoreSnapshot: null
    })
  }, [bootstrapQuery.data, caseId])

  useEffect(() => {
    if (!bootstrapQuery.isError) return
    useAnnotationStore.getState().reset()
    useHistoryStore.getState().reset()
    setViewerSessionState({
      submissionId: null,
      pendingRestoreSnapshot: null
    })
  }, [bootstrapQuery.isError])

  const previousCaseIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prev = previousCaseIdRef.current
    previousCaseIdRef.current = caseId
    // Skip initial mount (undefined → value); clear stores on real caseId transitions
    // so the timeline UI doesn't show the previous case's checkpoints during bootstrap.
    if (prev && prev !== caseId) {
      useAnnotationStore.getState().reset()
      useHistoryStore.getState().reset()
      setViewerSessionState({
        submissionId: null,
        pendingRestoreSnapshot: null
      })
    }
    return () => {
      useAnnotationStore.getState().reset()
      useHistoryStore.getState().reset()
    }
  }, [caseId])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const cmd = event.metaKey || event.ctrlKey
      if (!cmd) return

      if (event.key.toLowerCase() === "z" && !event.shiftKey) {
        event.preventDefault()
        applySnapshot(useHistoryStore.getState().undo())
      }

      if (event.key.toLowerCase() === "z" && event.shiftKey) {
        event.preventDefault()
        applySnapshot(useHistoryStore.getState().redo())
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [applySnapshot])

  const isViewOnlyMode =
    !(isReviewMode || isBootstrappingSession) && bootstrapQuery.data !== undefined && !bootstrapQuery.data.submission

  const startAnnotating = useCallback(async () => {
    if (!caseId) return
    await api(`/cases/${caseId}/submissions`, { method: "POST" })
    await queryClient.invalidateQueries({
      queryKey: ["pathology-annotation-bootstrap", caseId, projectId, currentUserRole]
    })
  }, [api, caseId, currentUserRole, projectId, queryClient])

  return {
    submissionId: effectiveSubmissionId,
    getAuthHeaders: effectiveGetAuthHeaders,
    applySnapshot: isReviewMode ? reviewBootstrap.applySnapshot : applySnapshot,
    handleTimelineScrub: isReviewMode ? reviewBootstrap.handleTimelineScrub : handleTimelineScrub,
    uploadHistoryNow,
    isHistoryDirty,
    isUploadingHistory,
    checkpointCount,
    isBootstrappingSession,
    markActiveModuleComplete,
    setModuleCompletionRemote,
    // Review-specific state
    isReviewMode,
    reviewEditEnabled,
    reviewSubmission: isReviewMode ? reviewBootstrap.submission : null,
    reviewStatus,
    setReviewStatus,
    setReviewEditEnabled: reviewBootstrap.setEditEnabled,
    refetchReviewSubmission: reviewBootstrap.refetchSubmission,
    // View-only mode state
    isViewOnlyMode,
    startAnnotating
  }
}
